import { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { MessagingService, UserService } from '../services/api';
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { User } from '../types';
import { DISABLE_ASYNC_EFFECTS } from '../debugConfig';

interface Message {
  id: string;
  sender: 'user' | 'friend';
  text: string;
  time: string;
  timestamp: number;
  sharedYap?: any; // Keeping loose type for now to match existing usage
  groupInvite?: any;
  audioUrl?: string;
  audioDuration?: number;
  imageUrl?: string;
  isProvisional?: boolean;
  edited?: boolean;
  read?: boolean;
  likes?: number;
  likedBy?: string[];
  replyTo?: { id: string; text: string; senderId: string; type?: 'text' | 'image' | 'voice' };
}

/**
 * Robustly checks if a sender ID belongs to the current user.
 * Normalizes for UID vs Username, case-insensitivity, and @ prefix.
 */
const getIsSelf = (senderId: string | undefined, firebaseUser: any, userProfile: any): boolean => {
  if (!senderId || !firebaseUser) return false;
  
  const uid = String(firebaseUser.uid).trim().toLowerCase();
  const sid = String(senderId).trim().toLowerCase();
  const username = userProfile?.username?.trim().toLowerCase();
  const cleanUsername = username?.replace('@', '');
  const cleanSid = sid.replace('@', '');

  return (
    sid === uid || 
    (username && sid === username) || 
    (cleanUsername && cleanSid === cleanUsername)
  );
};

interface Chat {
  id: string; // Firestore Chat ID
  friendId: string; // The other participant's ID
  name: string;
  avatar: string;
  lastMessage: string;
  time: string;
  unread: number;
  online: boolean;
  username?: string;
  country?: string;
  countryFlag?: string;
  rank?: string;
  messages: Message[];
}

interface MessagingContextType {
  chats: Chat[];
  /** False until the first chat snapshot lands, so an empty list can be told from an unloaded one. */
  chatsLoaded: boolean;
  messageRequests: Chat[];
  requestCount: number;
  getChat: (friendId: number | string) => Chat | undefined;
  addMessage: (friendId: number | string, message: Message, isRequest?: boolean) => Promise<void>;
  createOrUpdateChat: (chatData: any) => void; // Deprecated but kept for compatibility
  getChatMessages: (friendId: number | string) => Message[];
  enterChat: (friendId: string) => Promise<string>; // Returns chatId
  leaveChat: (chatId: string) => void;
  unreadCounts: Record<string, number>;
  latestMessages: Record<string, string>;
  totalUnread: number;
  resetAllUnreadCounts: () => void;
  deleteChat: (chatId: string) => Promise<void>;
  deleteMessage: (chatId: string, messageId: string) => Promise<void>;
  editMessage: (chatId: string, messageId: string, newText: string) => Promise<void>; // ✏️ Added
  acceptRequest: (chatId: string) => Promise<void>;
  declineRequest: (chatId: string) => Promise<void>;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export function MessagingProvider({ children }: { children: ReactNode }) {
  const { firebaseUser, friends, userProfile } = useUserData();
  const [chats, setChats] = useState<Chat[]>([]);
  const [messageRequests, setMessageRequests] = useState<Chat[]>([]);
  const [messagesCache, setMessagesCache] = useState<Record<string, Message[]>>({});
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const activeChatIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  // Subscription cleanup refs
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [latestMessages, setLatestMessages] = useState<Record<string, string>>({});
  const [totalUnread, setTotalUnread] = useState(0);

  // Subscription cleanup refs
  const chatSubscriptionRef = useRef<(() => void) | null>(null);
  const messageSubscriptionRef = useRef<(() => void) | (() => void)[] | null>(null);
  const unreadListenersRef = useRef<Record<string, () => void>>({});
  
  // 🔒 PATCH: Use ref for userProfile to avoid re-subscribing when profile updates
  const userProfileRef = useRef(userProfile);
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  // 🔒 PATCH: Cache for users not in friends list (to fix "Unknown User")
  const [nonFriendCache, setNonFriendCache] = useState<Record<string, User>>({});
  const [rawChats, setRawChats] = useState<any[]>([]);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const pendingFetches = useRef<Set<string>>(new Set());

    // 1. Subscribe to User's Chats (Raw Data)
    useEffect(() => {
        // ASYNC GUARD
        if (DISABLE_ASYNC_EFFECTS) {
            console.log("ASYNC EFFECT SKIPPED: Messaging.Chats");
            return;
        }

        if (!firebaseUser) {
            setChats([]);
            setRawChats([]);
            setChatsLoaded(true);
            return;
        }

        // 🔒 PATCH: Dual Subscription - Listen for both UID-based and Username-based chats
        // This is critical for legacy accounts where 'participants' contained the username.
        const subs: (() => void)[] = [];
        
        const setup = async () => {
            const rawResults: Record<string, any[]> = {};
            
            const updateRaw = () => {
                const all = Object.values(rawResults).flat();
                // Unique by ID
                const unique = Array.from(new Map(all.map(c => [c.id, c])).values());
                setRawChats(unique);
                // The first snapshot is what separates "no chats yet" from "not
                // loaded yet". ChatScreen showed the empty state for both.
                setChatsLoaded(true);
            };

            // 1. Sub by UID
            const unsubUID = await MessagingService.subscribeToUserChats(firebaseUser.uid, (firestoreChats) => {
                rawResults['uid'] = firestoreChats;
                updateRaw();
            });
            subs.push(unsubUID);

            // 2. Sub by Username Variations (Legacy catch-all)
            const baseUsername = userProfile?.username || '';
            const variants = new Set<string>();
            if (baseUsername) {
                variants.add(baseUsername);
                variants.add(baseUsername.toLowerCase());
                variants.add(baseUsername.replace('@', ''));
                variants.add(baseUsername.replace('@', '').toLowerCase());
            }

            for (const variant of Array.from(variants)) {
                if (variant === firebaseUser.uid) continue;
                // [Removed PII log: was logging username variant]
                const unsub = await MessagingService.subscribeToUserChats(variant, (firestoreChats) => {
                    rawResults[`name_${variant}`] = firestoreChats;
                    updateRaw();
                });
                subs.push(unsub);
            }
        };

        setup();

        return () => {
            subs.forEach(unsub => unsub());
            Object.values(unreadListenersRef.current).forEach(unsub => unsub());
            unreadListenersRef.current = {};
        };
    }, [firebaseUser, userProfile?.username]);

  // 🔎 TRACKING: Map of Canonical Key -> Set of relevant Chat IDs
  const canonicalToIdsRef = useRef<Map<string, string[]>>(new Map());

  // 1.5 Process Chats (Map Data & Fetch Missing Profiles)
  useEffect(() => {
     if (!rawChats.length || !firebaseUser) {
         if (chats.length > 0) setChats([]);
         return;
     }

     // A. Identify missing profiles logic
     const missingUserIds = new Set<string>();
     
     rawChats.forEach(c => {
        if (!c || !c.participants || !Array.isArray(c.participants)) return;
        const otherId = c.participants.find((p: string) => p && p !== firebaseUser.uid);
        if (otherId) {
            const isFriend = friends.some(f => String(f.id) === String(otherId));
            const isCached = !!nonFriendCache[otherId];
            if (!isFriend && !isCached && !pendingFetches.current.has(otherId)) {
                missingUserIds.add(otherId);
            }
        }
    });

    // B. Fetch missing profiles (Side Effect)
    if (missingUserIds.size > 0) {
        // [Removed PII log: was logging missing user profile IDs]
        
        // Mark as pending immediately to prevent duplicate fetches
        missingUserIds.forEach(id => pendingFetches.current.add(id));

        const fetchProfiles = async () => {
             // Dynamic import to avoid circular dep issues
             const { getDoc, doc } = await import('firebase/firestore');
             const { getDbSafe } = await import('../services/firebaseConfig');
             const db = await getDbSafe();

             const newCacheMatches: Record<string, User> = {};
             
             await Promise.all(Array.from(missingUserIds).map(async (uid) => {
                 try {
                     const userDoc = await getDoc(doc(db, 'users', uid));
                     if (userDoc.exists()) {
                         newCacheMatches[uid] = { id: userDoc.id, ...userDoc.data() } as User;
                     }
                 } catch (e) {
                     console.warn(`[MessagingContext] Failed to fetch profile for ${uid}`, e);
                 } finally {
                     // We keep it in pending for a bit or forever for this session to avoid retry loops on failure
                     // pendingFetches.current.delete(uid); 
                 }
             }));
             
             if (Object.keys(newCacheMatches).length > 0) {
                 setNonFriendCache(prev => ({ ...prev, ...newCacheMatches }));
             }
        };
        fetchProfiles();
    }

    // C. Map Chats — FILTER OUT pending request chats so they only appear in Requests inbox
    const mappedChats = rawChats
      .filter(c => {
        // Filter out completely corrupted chats
        if (!c || !c.id) return false;
        if (!Array.isArray(c.participants) || c.participants.length === 0) return false;
        
        // 🔒 LAZY FILTER: Hide chats with no message history (ghost chats)
        // Ensure we don't hide chats that have an audio snippet or recent activity.
        if (!c.lastMessage && !latestMessages[c.id]) return false;

        // 🔒 REQUEST FILTER: Exclude pending requests from main list
        if (c.isRequest === true && c.requestStatus === 'pending') return false;
        
        return true;
      })
      .map(c => {
        // Find the "other" participant with defensive checks
        let otherId = c.participants.find((p: string) => p && String(p).trim().toLowerCase() !== String(firebaseUser.uid).trim().toLowerCase());
        otherId = otherId ? String(otherId).trim() : firebaseUser.uid;

        // Find friend details (check friends list OR cache)
        // Check both ID and Username for legacy support
        let friend = friends.find(f => 
            (f.id && String(f.id).trim().toLowerCase() === String(otherId).toLowerCase()) ||
            (f.username && String(f.username).trim().toLowerCase() === String(otherId).toLowerCase())
        );
        
        // Fallback to cache if not a friend
        if (!friend && otherId && nonFriendCache[otherId]) {
            friend = nonFriendCache[otherId];
        }

        // Special case: If messaging self, use current profile if friend search failed
        if (!friend && String(otherId).toLowerCase() === String(firebaseUser.uid).toLowerCase()) {
            friend = userProfile as unknown as User;
        }

        // 🔒 NORMALIZATION: If we resolved a friend, force otherId to be their canonical UID
        // This ensures that "muhammed" (username) and "UID_123" map to the same friendId key
        if (friend && friend.id) {
            otherId = String(friend.id);
        }

        // Format time with defensive timestamp handling
        let timeDisplay = '';
        let rawTimestamp = 0;
        if (c.timestamp) {
          try {
            const date = c.timestamp.toDate ? c.timestamp.toDate() : new Date(c.timestamp);
            rawTimestamp = date.getTime();
            const now = Date.now();
            if (rawTimestamp > now + 3600000 * 24 || rawTimestamp < now - 3600000 * 24 * 365) {
                // Out of range, keep 0 as fallback or use now if recent but offset?
                // For now, if it's within a day of future (clock skew) or a year of past, it's valid.
            }
            timeDisplay = date.toISOString();
          } catch (e) {
            console.error('[MessagingContext] Invalid timestamp in chat:', c.id, e);
          }
        }

        return {
          id: c.id,
          friendId: otherId,
          name: friend?.name || 'Unknown User',
          avatar: UserService.resolveUserAvatar(friend), 
          lastMessage: typeof c.lastMessage === 'string' ? c.lastMessage : '',
          time: timeDisplay,
          rawTimestamp, // 🕒 ADDED: For robust sorting/dedup
          unread: 0,
          online: false,
          username: friend?.username,
          country: friend?.country,
          countryFlag: friend?.countryFlag,
          rank: friend?.rank,
          messages: []
        } as Chat & { rawTimestamp: number };
      });

    // 🕵️ PASS 1: Identity Resolver (Username -> UID)
    // Build a map of usernames to UIDs from all available sources
    const usernameToUid = new Map<string, string>();
    
    // 1A. Add from friends list
    friends.forEach(f => {
      if (f.username && f.id) {
          usernameToUid.set(f.username.toLowerCase(), String(f.id));
      }
    });

    // 1B. Add from cache
    Object.values(nonFriendCache).forEach(u => {
      if (u.username && u.id) {
          usernameToUid.set(u.username.toLowerCase(), String(u.id));
      }
    });

    // 1C. Extract from mappedChats (Identity Bridge)
    mappedChats.forEach(c => {
      if (c.friendId.length >= 20 && c.username) {
        usernameToUid.set(c.username.toLowerCase(), c.friendId);
      }
    });

    // [Removed PII log: was logging Identity Resolver username->UID map]

    // 🕵️ PASS 2: Normalization & Canonical Key Generation
    const normalizedChats = mappedChats.map(c => {
      let friendId = c.friendId;
      let username = c.username ? c.username.toLowerCase() : null;

      // 1. Resolve UID/Username linkage
      if (friendId.length < 20 && usernameToUid.has(friendId.toLowerCase())) {
          // It's a legacy username ID - find its UID
          friendId = usernameToUid.get(friendId.toLowerCase())!;
      }

      // 2. Resolve Username from friendId for display stability
      if (!username) {
          for (const [name, id] of usernameToUid.entries()) {
              if (id === friendId) {
                  username = name;
                  break;
              }
          }
      }

      // 3. Generate Canonical Merge Key (STABLE: Always prefer UID)
      // This ensures "muhammed" and "UID_123" BOTH map to "UID_123"
      let canonicalKey = friendId.length >= 20 ? friendId : (username || friendId).toLowerCase();

      // 🔍 PASS 3: Aggressive Mapping Check
      // If we STILL don't have a UID key, check the general user caches one more time
      if (canonicalKey.length < 20) {
        const lowercaseEntry = (username || friendId).toLowerCase();
        // Check friends
        const friendMatch = friends.find(f => f.username?.toLowerCase() === lowercaseEntry || String(f.id).toLowerCase() === lowercaseEntry);
        if (friendMatch) {
            canonicalKey = String(friendMatch.id);
            friendId = canonicalKey; // Upgrade friendId too
        } else {
          // Check nonFriendCache
          const cacheMatch = Object.values(nonFriendCache).find(u => u.username?.toLowerCase() === lowercaseEntry);
          if (cacheMatch) {
              canonicalKey = String(cacheMatch.id);
              friendId = canonicalKey;
          }
        }
      }

      // console.log(`[MessagingContext] Normalized Chat: ID=${c.id} | FriendID=${friendId} | Key=${canonicalKey}`);

      return { ...c, friendId, canonicalKey, username: username || c.username };
    });

    // 🔒 PATCH: Deduplicate UI using Canonical Key & Aggregate Metadata
    const dedupMap = new Map<string, Chat & { canonicalKey: string; rawTimestamp: number }>();
    
    normalizedChats.forEach(c => {
        const key = c.canonicalKey;
        if (!key || key === 'unknown') {
            dedupMap.set(c.id, c);
            return;
        }

        const existing = dedupMap.get(key);
        
        // 🕒 PROVISIONAL PROMOTION: Treat null/0 timestamp as "Now" (infinity)
        const ts = (chat: any) => chat.rawTimestamp || (Date.now() + 1000 * 60 * 60 * 24);

        if (!existing) {
            dedupMap.set(key, c);
        } else {
            const incomingTs = ts(c);
            const existingTs = ts(existing);

            // Winner selection
            if (incomingTs > existingTs) {
                const updated = { ...c };
                // 📦 METADATA INHERITANCE: If the newer thread is empty (e.g. fresh master),
                // inherit the snippet from the older thread for sidebar display.
                if (!updated.lastMessage && existing.lastMessage) {
                    updated.lastMessage = existing.lastMessage;
                }
                dedupMap.set(key, updated);
            } else {
                // Existing is newer or equal
                if (!existing.lastMessage && c.lastMessage) {
                    existing.lastMessage = c.lastMessage;
                }
            }
        }
    });

    const finalChats = Array.from(dedupMap.values());
    
    // Update Tracking Ref for message aggregation (Virtual Threads)
    const newCanonicalMap = new Map<string, string[]>();
    normalizedChats.forEach(c => {
      const ids = newCanonicalMap.get(c.canonicalKey) || [];
      if (!ids.includes(c.id)) {
          newCanonicalMap.set(c.canonicalKey, [...ids, c.id]);
      }
    });
    canonicalToIdsRef.current = newCanonicalMap;

    // [Removed PII logs: were logging canonical map and dedup summary]

    setChats(finalChats.sort((a, b) => {
        const tsA = a.rawTimestamp || (Date.now() + 100000);
        const tsB = b.rawTimestamp || (Date.now() + 100000);
        return tsB - tsA;
    }));

  }, [rawChats, friends, nonFriendCache, firebaseUser, userProfile, latestMessages]);

  // 1.6 Subscribe to Message Requests (separate from main chats)
  useEffect(() => {
    if (DISABLE_ASYNC_EFFECTS || !firebaseUser) return;
    let unsub: (() => void) | null = null;
    const setup = async () => {
      unsub = await MessagingService.subscribeToMessageRequests(firebaseUser.uid, (rawRequests) => {
        // Map to the same Chat shape used by the main list
        const mapped = rawRequests.map((c: any) => {
          const otherId = (c.participants || []).find((p: string) => p !== firebaseUser.uid) || firebaseUser.uid;
          const friend = friends.find(f => String(f.id) === String(otherId)) || nonFriendCache[otherId];
          return {
            id: c.id,
            friendId: otherId,
            name: friend?.name || 'Unknown User',
            avatar: friend ? (friend as any).croppedAvatar || (friend as any).avatar || (friend as any).image || '' : '',
            lastMessage: c.lastMessage || '',
            time: c.timestamp?.toDate ? c.timestamp.toDate().toISOString() : '',
            unread: 0,
            online: false,
            username: friend?.username,
            country: friend?.country,
            countryFlag: (friend as any)?.countryFlag,
            rank: (friend as any)?.rank,
            messages: [],
            isRequest: true,
            requestSenderId: c.requestSenderId,
          } as Chat & { isRequest: boolean; requestSenderId: string };
        });
        setMessageRequests(mapped);
      });
    };
    setup();
    return () => { if (unsub) unsub(); };
  }, [firebaseUser, friends, nonFriendCache]);

  // 1.5 Subscribe to Unread Counts for ALL raw chat documents
  useEffect(() => {
    if (DISABLE_ASYNC_EFFECTS || !firebaseUser) return;

    // We subscribe to EVERY chat in rawChats to ensure we don't miss notifications 
    // from duplicate/legacy documents.
    rawChats.forEach(async (chat) => {
      if (unreadListenersRef.current[chat.id]) return;

      const unsubscribe = await MessagingService.subscribeToMessages(chat.id, (messages) => {
        const isSelf = (senderId: string) => getIsSelf(senderId, firebaseUser, userProfileRef.current);

        let unread = 0;
        if (activeChatIdRef.current === chat.id) {
           unread = 0;
        } else {
           unread = messages.filter(m => !isSelf(m.senderId) && !m.read).length;
        }

        setUnreadCounts(prev => {
          if (prev[chat.id] === unread) return prev;
          return { ...prev, [chat.id]: unread };
        });

        // Also capture the latest message text for chat list preview
        if (messages.length > 0) {
          const lastMsg = messages[messages.length - 1];
          const lastText = lastMsg.sharedYap
            ? (lastMsg.text || '🔗 Shared a Yap')
            : (lastMsg.audioUrl ? '🎙️ Voice Message' : (lastMsg.imageUrl ? '📷 Image' : (lastMsg.text || '')));
          setLatestMessages(prev => {
            if (prev[chat.id] === lastText) return prev;
            return { ...prev, [chat.id]: lastText };
          });
        }
      });

      unreadListenersRef.current[chat.id] = unsubscribe;
    });

    // Cleanup logic is already in Subscriptions effect
  }, [rawChats, firebaseUser]);


  /**
   * Mark every conversation read, on the server as well as on screen.
   *
   * This used to be `() => setUnreadCounts({})` — local state and nothing else.
   * Unread is derived live from the per-message `read` flags
   * (`messages.filter(m => !isSelf(m.senderId) && !m.read).length`), so clearing
   * the counters wrote nothing anyone could observe: the next snapshot from any
   * chat listener recomputed from the untouched flags and every count came
   * straight back. That is the Messages half of "mark all read does not stick".
   *
   * The counters are still cleared immediately so the badges drop at once, and
   * the writes follow; the listeners then confirm what was already shown rather
   * than contradicting it.
   */
  const resetAllUnreadCounts = () => {
    setUnreadCounts({});
    if (!firebaseUser) return;

    const uid = firebaseUser.uid;
    // Every chat in rawChats, not just the mapped ones: rawChats includes the
    // duplicate and legacy documents the dual subscription exists to catch, and
    // their messages count toward the badge too.
    const chatIds = Array.from(new Set(rawChats.map((c: any) => String(c.id)).filter(Boolean)));

    void (async () => {
      const { MessagingService } = await import('../services/api');
      for (const chatId of chatIds) {
        try {
          await MessagingService.markMessagesAsRead(chatId, uid);
        } catch (e) {
          // One unreadable conversation must not stop the rest being cleared.
          console.warn(`[Messaging] Could not mark ${chatId} read:`, e);
        }
      }
    })();
  };

  // 1.7 Calculate Total Unread whenever unreadCounts change
  useEffect(() => {
    const total = Object.values(unreadCounts).reduce((acc, count) => acc + count, 0);
    setTotalUnread(total);
    // [Removed log: was logging total unread count]
  }, [unreadCounts]);

  // Track the latest requested chat to prevent race conditions
  const latestChatIdRef = useRef<string | null>(null);

  // 2. Enter Chat (Subscribe to ALL relevant threads for this friend)
  const enterChat = async (friendId: string): Promise<string> => {
    if (!firebaseUser) throw new Error("Not logged in");

    // 1. Find the Master Chat ID (Deterministic)
    // 🔒 LAZY: Pass true to skip document creation in Firestore until a message is sent.
    const masterChatId = await MessagingService.createChat(firebaseUser.uid, friendId, true);
    latestChatIdRef.current = masterChatId;

    // 2. Identify ALL Relevant Chat IDs (Merge legacy + master)
    const chat = chats.find(c => String(c.friendId) === String(friendId));
    const allRelevantIds = new Set<string>();
    allRelevantIds.add(masterChatId);
    
    if (chat && (chat as any).canonicalKey) {
        const dupIds = canonicalToIdsRef.current.get((chat as any).canonicalKey) || [];
        dupIds.forEach(id => allRelevantIds.add(id));
    }

    // If already active on the master, skip re-sub? 
    // Actually we need to make sure we watch all relevant ones if the set changed.
    
    // Cleanup previous subscriptions
    if (Array.isArray(messageSubscriptionRef.current)) {
      messageSubscriptionRef.current.forEach(u => u());
    } else if (messageSubscriptionRef.current) {
      messageSubscriptionRef.current();
    }
    messageSubscriptionRef.current = [];

    setActiveChatId(masterChatId);

    // 3. Subscribe to EVERY relevant thread in parallel
    const unsubscritpions: (() => void)[] = [];
    
    for (const id of Array.from(allRelevantIds)) {
        const unsub = await MessagingService.subscribeToMessages(id, async (firestoreMessages) => {
          // Mark as read if found unread
          const hasUnread = firestoreMessages.some(m => !getIsSelf(m.senderId, firebaseUser, userProfileRef.current) && !m.read);
          if (hasUnread) {
            MessagingService.markMessagesAsRead(id, firebaseUser.uid).catch(console.error);
          }

          const mappedMessages = firestoreMessages.map(m => ({
            id: m.id,
            sender: getIsSelf(m.senderId, firebaseUser, userProfileRef.current) ? 'user' : 'friend',
            text: m.text,
            timestamp: typeof m.timestamp === 'number' ? m.timestamp : (m.timestamp?.toMillis ? m.timestamp.toMillis() : Date.now()),
            time: typeof m.time === 'string' ? m.time : (m.timestamp?.toDate ? m.timestamp.toDate().toISOString() : new Date().toISOString()),
            sharedYap: m.sharedYap,
            groupInvite: m.groupInvite,
            audioUrl: m.audioUrl,
            audioDuration: m.audioDuration,
            imageUrl: m.imageUrl,
            read: m.read,
            likes: m.likes ?? 0,
            likedBy: m.likedBy ?? [],
            replyTo: m.replyTo,
            isProvisional: !m.timestamp
          } as Message));

          setMessagesCache(prev => ({ ...prev, [id]: mappedMessages }));
        });
        unsubscritpions.push(unsub);
    }

    messageSubscriptionRef.current = unsubscritpions;
    return masterChatId;
  };

  const deleteChat = async (chatId: string) => {
    try {
      await MessagingService.deleteChat(chatId);
      setRawChats(prev => prev.filter(c => c.id !== chatId));
      toast.success("Conversation deleted");
    } catch (e) {
      console.error("[MessagingContext] Delete chat fail:", e);
      toast.error("Failed to delete conversation");
    }
  };

  const leaveChat = (chatId: string) => {
    if (activeChatId === chatId) {
      if (Array.isArray(messageSubscriptionRef.current)) {
        messageSubscriptionRef.current.forEach(u => u());
        messageSubscriptionRef.current = null;
      }
      setActiveChatId(null);
    }
  };

  // 3. Helper Methods
  const getChat = (friendId: number | string): Chat | undefined => {
    return chats.find(c => String(c.friendId) === String(friendId))
        || messageRequests.find(c => String(c.friendId) === String(friendId));
  };

  const getChatMessages = (friendId: number | string): Message[] => {
    const chat = getChat(friendId);
    if (!chat) return [];
    
    // Aggregation Logic: Pull messages from ALL related chat IDs
    const relevantIds = canonicalToIdsRef.current.get((chat as any).canonicalKey) || [chat.id];
    const allMsgs: Message[] = [];
    
    relevantIds.forEach(id => {
      const msgs = messagesCache[id] || [];
      allMsgs.push(...msgs);
    });

    // Deduplicate and Sort
    const uniqueMap = new Map<string, Message>();
    allMsgs.forEach(m => uniqueMap.set(m.id, m));
    
    return Array.from(uniqueMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  const addMessage = async (friendId: number | string, message: Message, isRequest: boolean = false) => {
    if (!firebaseUser) return;

    // 🔒 SECURITY: Note — Core security checks (bans/blocks) are now delegated
    // to MessagingService.sendMessage to avoid redundant Firestore round-trips.

    // Ensure chat exists (idempotent), stamping isRequest flag on first creation
    const chatId = await MessagingService.createChat(firebaseUser.uid, String(friendId), false, isRequest);

    // Send message
    const additionalData: any = {};
    if (message.sharedYap) additionalData.sharedYap = message.sharedYap;
    if (message.groupInvite) additionalData.groupInvite = message.groupInvite;
    if (message.audioUrl) {
      additionalData.audioUrl = message.audioUrl;
      additionalData.audioDuration = message.audioDuration;
    }
    if (message.imageUrl) additionalData.imageUrl = message.imageUrl;
    if (message.replyTo) additionalData.replyTo = message.replyTo;
    
    await MessagingService.sendMessage(chatId, firebaseUser.uid, message.text, Object.keys(additionalData).length > 0 ? additionalData : undefined);
  };

  const createOrUpdateChat = (_chatData: any) => {
    // Deprecated: Logic handled by addMessage/enterChat and Firestore listeners
    // [Removed PII log: was logging chatData]
  };

  const acceptRequest = async (chatId: string) => {
    const { MessagingService } = await import('../services/api');
    await MessagingService.acceptMessageRequest(chatId);
    // Move from requests to main chat list
    setMessageRequests(prev => prev.filter(r => r.id !== chatId));
    toast.success('Message request accepted');
  };

  const declineRequest = async (chatId: string) => {
    const { MessagingService } = await import('../services/api');
    await MessagingService.archiveMessageRequest(chatId);
    setMessageRequests(prev => prev.filter(r => r.id !== chatId));
    toast.success('Request declined');
  };

  return (
    <MessagingContext.Provider
      value={{
        chats,
        chatsLoaded,
        messageRequests,
        requestCount: messageRequests.length,
        getChat,
        addMessage,
        createOrUpdateChat,
        getChatMessages,
        enterChat,
        leaveChat,
        unreadCounts,
        latestMessages,
        totalUnread,
        resetAllUnreadCounts,
        deleteChat,
        deleteMessage: MessagingService.deleteMessage,
        editMessage: MessagingService.editMessage, // ✏️ Exposed
        acceptRequest,
        declineRequest,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
}

export function useMessaging() {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
}
